import React, { useEffect, useState } from "react";
import { X, Crown, Sparkles, Loader2, CheckCircle2, Mail, Phone } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { publicApi } from "../lib/api";

export default function SubscribeModal({ open, onClose }) {
  const { locale } = useI18n();
  const [plans, setPlans] = useState(null);
  const [plan, setPlan] = useState("pro");
  const [billing, setBilling] = useState("annual");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      publicApi.plans().then(({ data }) => setPlans(data.plans)).catch(() => {});
    }
  }, [open]);

  if (!open) return null;

  const xaf = (n) => n.toLocaleString("fr-FR") + " XAF";
  const monthlyPrice = plans?.[plan]?.monthly;
  const annualPrice = plans?.[plan]?.annual;
  const chosenPrice = billing === "monthly" ? monthlyPrice : annualPrice;
  const annualSaving =
    monthlyPrice && annualPrice
      ? Math.round(((monthlyPrice * 12 - annualPrice) / (monthlyPrice * 12)) * 100)
      : 0;

  const submit = async (e) => {
    e?.preventDefault();
    setError("");
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError(locale === "fr" ? "Adresse e-mail invalide." : "Invalid email address.");
      return;
    }
    // Validation du numéro de téléphone (format international simplifié)
    const phoneClean = phone.replace(/\D/g, "");
    if (phoneClean.length < 9 || phoneClean.length > 15) {
      setError(locale === "fr" ? "Numéro de téléphone invalide (9-15 chiffres)." : "Invalid phone number (9-15 digits).");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await publicApi.subscribe({
        plan,
        billing,
        email: email.trim(),
        phone: phoneClean,
        language: locale,
      });
      // Store the pending deposit id so /payment/success can poll
      localStorage.setItem("lexhab_pending_deposit", data.deposit_id);
      // Redirect to the PawaPay page (in demo mode it's our own /payment/success?demo=true)
      window.location.href = data.redirect_url;
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          (locale === "fr" ? "Impossible de lancer le paiement. Réessayez." : "Cannot initiate payment.")
      );
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0D1B2A]/80 backdrop-blur-sm animate-fade-in"
      data-testid="subscribe-modal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="relative w-full max-w-3xl bg-white border border-primary/20 rounded-sm shadow-2xl max-h-[92vh] overflow-y-auto">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center text-foreground/50 hover:text-foreground hover:bg-primary/5 rounded-sm transition-colors"
          data-testid="subscribe-modal-close"
        >
          <X size={16} />
        </button>

        <div className="p-6 md:p-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-accent mb-2">
            / abonnement
          </div>
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-primary leading-tight">
            {locale === "fr"
              ? "Débloquez les questions illimitées"
              : "Unlock unlimited questions"}
          </h2>
          <p className="text-sm text-foreground/60 mt-1">
            {locale === "fr"
              ? "Paiement sécurisé via Mobile Money (MTN ou Orange) — Cameroun"
              : "Secure payment via Mobile Money (MTN or Orange) — Cameroon"}
          </p>

          {/* Plan cards */}
          <div className="grid md:grid-cols-2 gap-3 mt-6" data-testid="plan-cards">
            <PlanCard
              active={plan === "standard"}
              onClick={() => setPlan("standard")}
              title="Standard"
              subtitle={
                locale === "fr"
                  ? "Étudiants, chercheurs, citoyens avertis"
                  : "Students, researchers, informed citizens"
              }
              monthly={plans?.standard?.monthly}
              annual={plans?.standard?.annual}
              billing={billing}
              icon={Sparkles}
              testid="plan-standard"
            />
            <PlanCard
              active={plan === "pro"}
              onClick={() => setPlan("pro")}
              title="Pro"
              subtitle={
                locale === "fr"
                  ? "Notaires, avocats, promoteurs, architectes"
                  : "Notaries, lawyers, developers, architects"
              }
              monthly={plans?.pro?.monthly}
              annual={plans?.pro?.annual}
              billing={billing}
              icon={Crown}
              accent
              testid="plan-pro"
            />
          </div>

          {/* Billing toggle */}
          <div className="mt-5 flex items-center gap-2 flex-wrap" data-testid="billing-toggle">
            <BillingPill
              active={billing === "monthly"}
              onClick={() => setBilling("monthly")}
              label={locale === "fr" ? "Mensuel" : "Monthly"}
              testid="billing-monthly"
            />
            <BillingPill
              active={billing === "annual"}
              onClick={() => setBilling("annual")}
              label={
                (locale === "fr" ? "Annuel" : "Annual") +
                (annualSaving > 0 ? ` · -${annualSaving}%` : "")
              }
              testid="billing-annual"
            />
            {annualSaving > 0 && billing === "annual" && (
              <span className="font-mono text-[10px] uppercase tracking-wider text-[#065F46] bg-[#D1FAE5] px-2 py-1 rounded-sm">
                {locale === "fr"
                  ? `Économisez ${annualSaving}%`
                  : `Save ${annualSaving}%`}
              </span>
            )}
          </div>

          {/* Email + Phone + submit */}
          <form onSubmit={submit} className="mt-6">
            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 mb-1.5">
              {locale === "fr" ? "Votre adresse e-mail" : "Your email address"}
            </label>
            <div className="flex items-center gap-2 border border-primary/20 rounded-sm px-3 focus-within:border-primary transition-colors mb-4">
              <Mail size={14} className="text-foreground/40" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.cm"
                className="flex-1 py-2.5 outline-none bg-transparent text-sm"
                data-testid="subscribe-email-input"
              />
            </div>

            <label className="block font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/60 mb-1.5">
              {locale === "fr" ? "Numéro de téléphone (Mobile Money)" : "Phone number (Mobile Money)"}
            </label>
            <div className="flex items-center gap-2 border border-primary/20 rounded-sm px-3 focus-within:border-primary transition-colors">
              <Phone size={14} className="text-foreground/40" />
              <input
                type="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="67X XXX XXXX"
                className="flex-1 py-2.5 outline-none bg-transparent text-sm"
                data-testid="subscribe-phone-input"
              />
            </div>

            {error && (
              <div
                className="mt-3 text-sm text-[#991B1B] bg-[#FEE2E2] border border-[#FECACA] px-3 py-2 rounded-sm"
                data-testid="subscribe-error"
              >
                {error}
              </div>
            )}

            <div className="mt-6 flex flex-col md:flex-row md:items-center justify-between gap-3">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-foreground/50">
                  {locale === "fr" ? "À payer" : "Total due"}
                </div>
                <div className="font-display text-3xl font-semibold text-primary">
                  {chosenPrice ? xaf(chosenPrice) : "—"}
                </div>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 bg-primary text-white px-6 py-3 rounded-sm font-medium text-sm hover:bg-primary-light transition-colors disabled:opacity-60 w-full md:w-auto"
                data-testid="subscribe-submit-btn"
              >
                {submitting ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : null}
                {locale === "fr" ? "Payer via PawaPay" : "Pay via PawaPay"}
              </button>
            </div>

            <p className="mt-4 font-mono text-[10px] text-foreground/45 leading-relaxed">
              {locale === "fr"
                ? "Méthodes de paiement : MTN Mobile Money ou Orange Money (Cameroun, XAF). Vous recevrez un jeton d'accès qui débloque les questions illimitées sur cet appareil."
                : "Payment methods: MTN Mobile Money or Orange Money (Cameroon, XAF). You'll receive an access token that unlocks unlimited questions on this device."}
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}

function PlanCard({ active, onClick, title, subtitle, monthly, annual, billing, icon: Icon, accent, testid }) {
  const price = billing === "monthly" ? monthly : annual;
  const xaf = (n) => (n ? n.toLocaleString("fr-FR") + " XAF" : "—");
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`relative text-left p-4 rounded-sm border-2 transition-all ${
        active
          ? accent
            ? "border-accent bg-accent/5"
            : "border-primary bg-primary/5"
          : "border-primary/15 bg-white hover:border-primary/30"
      }`}
    >
      <div className="flex items-center gap-2 mb-1">
        <Icon size={15} className={accent ? "text-accent-dark" : "text-primary"} />
        <span className="font-display text-lg font-semibold text-foreground">{title}</span>
        {active && (
          <CheckCircle2
            size={14}
            className={`ml-auto ${accent ? "text-accent-dark" : "text-primary"}`}
          />
        )}
      </div>
      <p className="text-xs text-foreground/60 mb-3">{subtitle}</p>
      <div className="font-display text-2xl font-semibold text-primary">{xaf(price)}</div>
      <div className="font-mono text-[10px] uppercase tracking-wider text-foreground/45">
        {billing === "monthly" ? "/mois" : "/an"}
      </div>
    </button>
  );
}

function BillingPill({ active, onClick, label, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-all ${
        active
          ? "bg-primary text-white border-primary"
          : "bg-white text-foreground/70 border-primary/20 hover:border-primary"
      }`}
    >
      {label}
    </button>
  );
}
